import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ContactThread from "@/pages/contactos/components/ContactThread";
import { timeAgo } from "@/lib/timeAgo";
import {
  addPartnerContacts,
  fetchPartnerContacts,
  fetchPartnerState,
  invitePartnerContact,
  removeContact,
  setContactAgent,
  setPartnerAgent,
  type AddResult,
  type PartnerContact,
  type PartnerWaState,
} from "@/services/waPartner";

const inputCls = "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";
const cardCls = "bg-background-100 border border-background-200 rounded-2xl p-5";
const btnPrimary = "px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-40 cursor-pointer whitespace-nowrap";
const btnGhost = "px-3.5 py-1.5 rounded-full text-xs font-semibold border border-background-300 text-foreground-800 hover:bg-background-200 disabled:opacity-40 cursor-pointer whitespace-nowrap";

const COUNTRIES: [string, string][] = [
  ["CO", "Colombia (+57)"],
  ["MX", "México (+52)"],
  ["US", "Estados Unidos (+1)"],
  ["ES", "España (+34)"],
  ["AR", "Argentina (+54)"],
  ["PE", "Perú (+51)"],
  ["CL", "Chile (+56)"],
  ["EC", "Ecuador (+593)"],
  ["VE", "Venezuela (+58)"],
  ["PA", "Panamá (+507)"],
  ["DO", "Rep. Dominicana (+1)"],
  ["GT", "Guatemala (+502)"],
  ["BR", "Brasil (+55)"],
];

const TPL_LABEL: Record<string, string> = {
  APPROVED: "aprobada",
  PENDING: "en revisión de Meta",
  DRAFT: "pendiente de enviar a Meta",
  REJECTED: "rechazada por Meta",
};

function parseLines(raw: string): { name: string; phone: string }[] {
  const out: { name: string; phone: string }[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const l = line.trim();
    if (!l) continue;
    const m = l.match(/(\+?\d[\d\s().-]{6,}\d)/);
    if (!m) {
      out.push({ name: l, phone: "" });
      continue;
    }
    const phone = m[1].replace(/[^\d+]/g, "");
    const name = l.replace(m[1], "").replace(/[,;:\-–|]+/g, " ").replace(/\s+/g, " ").trim();
    out.push({ name, phone });
  }
  return out;
}

function Switch({ on, onChange, disabled, label }: { on: boolean; onChange: () => void; disabled?: boolean; label: string }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 cursor-pointer disabled:opacity-50 ${on ? "bg-primary-500" : "bg-background-300"}`}
    >
      <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`}></span>
    </button>
  );
}

export default function MiWhatsApp() {
  const [st, setSt] = useState<PartnerWaState | null>(null);
  const [contacts, setContacts] = useState<PartnerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [raw, setRaw] = useState("");
  const [country, setCountry] = useState("CO");
  const [consent, setConsent] = useState(false);
  const [invite, setInvite] = useState(true);
  const [results, setResults] = useState<AddResult[] | null>(null);
  const [filter, setFilter] = useState<"todos" | "on" | "off" | "pend">("todos");
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const notify = useCallback((m: string) => {
    setToast(m);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 3000);
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      setContacts(await fetchPartnerContacts());
    } catch {
      /* reintento */
    }
  }, []);

  useEffect(() => {
    (async () => {
      const r = (await fetchPartnerState()) as Partial<PartnerWaState> & { error?: string };
      if (r.error && !("agentEnabled" in r)) setErr(r.error);
      else setSt(r as PartnerWaState);
      await loadContacts();
      setLoading(false);
    })();
    const t = window.setInterval(() => void loadContacts(), 15000);
    return () => window.clearInterval(t);
  }, [loadContacts]);

  const parsed = useMemo(() => parseLines(raw), [raw]);
  const validCount = parsed.filter((p) => p.phone).length;

  const shown = contacts.filter((c) => (filter === "on" ? c.agentEnabled : filter === "off" ? !c.agentEnabled : filter === "pend" ? c.unread > 0 : true));

  const toggleMaster = async () => {
    if (!st) return;
    setBusy(true);
    const r = await setPartnerAgent(!st.agentEnabled);
    setBusy(false);
    if (r.ok) {
      setSt({ ...st, agentEnabled: !st.agentEnabled });
      notify(!st.agentEnabled ? "Agente encendido" : "Agente apagado: ya no responderá a nadie");
    } else notify(r.error ?? "No se pudo cambiar");
  };

  const add = async () => {
    setBusy(true);
    setResults(null);
    const r = await addPartnerContacts({ contacts: parsed.filter((p) => p.phone), consent, invite, defaultCountry: country });
    setBusy(false);
    if (r.ok && r.results) {
      setResults(r.results);
      if (r.results.some((x) => x.status === "invited" || x.status === "added")) setRaw("");
      await loadContacts();
    } else notify(r.error ?? "No se pudo agregar");
  };

  if (loading) {
    return (
      <AppShell headerVariant="onLight" showBack>
        <div className="max-w-3xl mx-auto px-4 py-10 text-center text-foreground-500">Cargando…</div>
      </AppShell>
    );
  }

  return (
    <AppShell headerVariant="onLight" showBack>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-5">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-foreground-950">Mi agente de WhatsApp</h1>
          <p className="text-foreground-600 text-sm mt-1">
            Un asistente que atiende por ti a las personas que tú elijas. Tú decides cuándo está encendido y con quién.
          </p>
        </div>

        {err ? <div className={`${cardCls} text-sm text-accent-700`}>{err}</div> : null}

        {st ? (
          <>
            <div className={cardCls}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-heading font-bold text-foreground-950">Responder automáticamente</div>
                  <p className="text-xs text-foreground-500 mt-1 max-w-md">
                    {st.agentEnabled
                      ? "Encendido: el asistente responde SOLO a los contactos de la lista de abajo que tengan el interruptor activo. A cualquier otra persona no le responde."
                      : "Apagado: nadie recibe respuestas automáticas. Los mensajes te llegan para que respondas tú."}
                  </p>
                </div>
                <Switch on={st.agentEnabled} onChange={() => void toggleMaster()} disabled={busy} label="Encender o apagar mi agente de WhatsApp" />
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-primary-400/60 bg-primary-500/5 p-3">
                  <div className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-primary-600"></i> Número oficial de Vívelo
                  </div>
                  <p className="text-[11px] text-foreground-600 mt-1">
                    Tus contactos reciben tu invitación desde el WhatsApp de Vívelo{st.sharedNumber ? ` (${st.sharedNumber})` : ""} y la conversación aparece aquí. Tu WhatsApp personal no se toca.
                  </p>
                </div>
                <div className="rounded-xl border border-dashed border-background-300 p-3 opacity-70">
                  <div className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-smartphone-line"></i> Mi propio WhatsApp Business
                    <span className="text-[10px] bg-amber-500/15 text-amber-700 px-2 py-0.5 rounded-full">Próximamente</span>
                  </div>
                  <p className="text-[11px] text-foreground-600 mt-1">Conectar tu número para usarlo a la vez en tu celular y con el asistente (coexistencia de Meta).</p>
                </div>
              </div>

              {!st.whatsappReady ? <p className="mt-3 text-xs text-amber-800 bg-amber-500/10 rounded-lg px-3 py-2">El WhatsApp de Vívelo todavía no está conectado (lo configura el administrador). Puedes ir agregando contactos; las invitaciones saldrán cuando esté listo.</p> : null}
              {st.whatsappReady && !st.globalAgentOn ? <p className="mt-3 text-xs text-amber-800 bg-amber-500/10 rounded-lg px-3 py-2">El administrador aún no activó el asistente de IA, así que por ahora las respuestas las das tú.</p> : null}
            </div>

            <div className={cardCls}>
              <h2 className="font-heading font-bold text-foreground-950">Agregar contactos</h2>
              <p className="text-xs text-foreground-500 mt-1">Un contacto por línea: nombre y número. Ej.: <span className="font-mono">Laura Pérez, 300 123 4567</span></p>
              <textarea
                rows={4}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={"Laura Pérez, 300 123 4567\nCarlos, +52 55 1234 5678"}
                className={`${inputCls} mt-3 resize-y font-mono text-xs`}
              />
              <div className="mt-3 grid sm:grid-cols-2 gap-3 items-center">
                <label className="block text-xs text-foreground-600">
                  País por defecto (si el número no trae +)
                  <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${inputCls} mt-1`}>
                    {COUNTRIES.map(([iso, label]) => (
                      <option key={iso} value={iso}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="text-xs text-foreground-500">{validCount ? `${validCount} contacto${validCount === 1 ? "" : "s"} detectado${validCount === 1 ? "" : "s"}` : "Aún no hay números"}</div>
              </div>
              <label className="mt-3 flex items-start gap-2 text-xs text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-4 h-4 mt-0.5 accent-primary-500" />
                <span>Confirmo que son personas que me conocen y aceptan que les escriba por WhatsApp. Si alguien pide no recibir más mensajes, se respeta de inmediato.</span>
              </label>
              <label className="mt-2 flex items-start gap-2 text-xs text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} className="w-4 h-4 mt-0.5 accent-primary-500" />
                <span>Enviarles ahora un mensaje de presentación del asistente (plantilla {TPL_LABEL[st.inviteTemplateStatus] ?? st.inviteTemplateStatus}). Máx. 30 por día.</span>
              </label>
              <button className={`${btnPrimary} mt-4`} disabled={busy || !consent || !validCount} onClick={() => void add()}>
                {busy ? "Agregando…" : "Agregar contactos"}
              </button>
              {results ? (
                <ul className="mt-4 space-y-1.5 text-xs">
                  {results.map((r, i) => (
                    <li key={i} className={`flex items-start gap-2 ${r.status === "invited" || r.status === "added" ? "text-foreground-700" : "text-accent-700"}`}>
                      <i className={r.status === "invited" ? "ri-send-plane-fill text-primary-600" : r.status === "added" ? "ri-checkbox-circle-line text-primary-600" : "ri-error-warning-line"}></i>
                      <span>
                        <b>{r.name || `+${r.phone.replace(/\D/g, "")}`}</b> · {r.status === "invited" ? "agregado e invitado" : r.status === "added" ? "agregado" : "no se agregó"}
                        {r.detail ? ` — ${r.detail}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className={cardCls}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading font-bold text-foreground-950">Mis contactos ({contacts.length})</h2>
                <div className="flex gap-1 flex-wrap">
                  {([["todos", "Todos"], ["on", "Con agente"], ["off", "Sin agente"], ["pend", "Sin leer"]] as const).map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer ${filter === k ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-700"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-foreground-500 mt-1">El interruptor de cada contacto controla si el asistente le responde. Apágalo cuando quieras atenderlo tú (por ejemplo, un amigo que te escribe por otro tema).</p>

              {!shown.length ? <p className="text-sm text-foreground-500 mt-4">{contacts.length ? "Ningún contacto en este filtro." : "Todavía no has agregado contactos."}</p> : null}
              <div className="mt-3 divide-y divide-background-200">
                {shown.map((c) => (
                  <div key={c.id} className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-foreground-950 truncate">{c.name || `+${c.phone}`}</span>
                          {c.unread > 0 ? <span className="bg-primary-500 text-background-950 text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">{c.unread}</span> : null}
                        </div>
                        <div className="text-[11px] text-foreground-500 flex flex-wrap gap-x-2">
                          <span>+{c.phone}</span>
                          <span>{c.invitedAt ? `Invitado ${timeAgo(c.invitedAt).toLowerCase()}` : "Sin invitar"}</span>
                          {c.lastMessageAt ? <span>Último mensaje {timeAgo(c.lastMessageAt).toLowerCase()}</span> : null}
                          {c.convStatus === "human" ? <span className="text-amber-700 font-semibold">Lo atiendes tú</span> : null}
                          {c.convStatus === "closed" ? <span>Cerrada</span> : null}
                          {!c.consent ? <span className="text-accent-700 font-semibold">Pidió no recibir mensajes</span> : null}
                        </div>
                        {c.preview ? <div className="text-xs text-foreground-600 truncate mt-0.5">{c.preview}</div> : null}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-foreground-500 hidden sm:inline">Agente</span>
                        <Switch
                          on={c.agentEnabled}
                          label={`Agente para ${c.name || c.phone}`}
                          disabled={!c.consent}
                          onChange={async () => {
                            try {
                              await setContactAgent(c.id, !c.agentEnabled);
                              setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, agentEnabled: !c.agentEnabled } : x)));
                            } catch {
                              notify("No se pudo cambiar");
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button className={btnGhost} onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                        {openId === c.id ? "Ocultar conversación" : "Ver conversación"}
                      </button>
                      {!c.invitedAt && c.consent ? (
                        <button
                          className={btnGhost}
                          onClick={async () => {
                            const r = await invitePartnerContact(c.id);
                            notify(r.ok ? "Invitación enviada" : r.error ?? "No se pudo enviar");
                            await loadContacts();
                          }}
                        >
                          Invitar
                        </button>
                      ) : null}
                      <button
                        className={`${btnGhost} !text-accent-700`}
                        onClick={async () => {
                          if (!window.confirm("¿Quitar este contacto de la lista del agente? El historial se conserva en tu pipeline.")) return;
                          try {
                            await removeContact(c.id);
                            await loadContacts();
                          } catch {
                            notify("No se pudo quitar");
                          }
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                    {openId === c.id ? (
                      <div className="mt-3">
                        {c.prospectId ? <ContactThread prospectId={c.prospectId} isWhatsApp notify={notify} /> : <p className="text-xs text-foreground-500">Este contacto todavía no tiene conversación.</p>}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-foreground-500 text-center">
              Ve cómo avanza cada persona hasta volverse socio en <Link to="/app/contactos" className="font-semibold text-primary-700 hover:underline">Contactos</Link>.
            </p>
          </>
        ) : null}
      </div>

      {toast ? <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] bg-background-950 text-white text-sm px-4 py-3 rounded-full">{toast}</div> : null}
    </AppShell>
  );
}
